import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Eye, FileText, Search, Calendar, X } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";

const statusLabels = {
  pending: "Pending",
  sent_to_worker: "Sent to Worker",
  received_from_worker: "Received from Worker",
  product_ready: "Product Ready",
  received: "Received"
};

export default function SlipsPage() {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const statusFilter = searchParams.get("status") || "";
  const upcomingFilter = searchParams.get("upcoming") === "true";
  const navigate = useNavigate();

  const fetchSlips = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      else if (upcomingFilter) params.upcoming = true;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get("/slips", { params });
      setSlips(res.data);
    } catch { toast.error("Failed to load slips"); }
    setLoading(false);
  }, [statusFilter, upcomingFilter, searchQuery]);

  useEffect(() => { fetchSlips(); }, [fetchSlips]);

  const handleFilterChange = (value) => {
    if (value === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ status: value });
    }
  };

  const clearUpcomingFilter = () => {
    setSearchParams({});
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div data-testid="slips-page" className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in-up">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">Slips</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage tailoring orders</p>
        </div>
        <Button data-testid="create-slip-btn" onClick={() => navigate("/slips/new")} className="btn-maroon rounded-sm gap-2 w-full sm:w-auto">
          <Plus className="w-4 h-4" /> New Slip
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by slip number, customer name, phone, or address..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10 bg-card border-border rounded-sm h-10 sm:h-9"
            data-testid="slip-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider hidden sm:inline">Filter:</span>
          <Select value={statusFilter || "all"} onValueChange={handleFilterChange}>
            <SelectTrigger data-testid="slip-status-filter" className="w-full sm:w-48 bg-card border-border rounded-sm h-10 sm:h-9">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent_to_worker">Sent to Worker</SelectItem>
              <SelectItem value="received_from_worker">Received from Worker</SelectItem>
              <SelectItem value="product_ready">Product Ready</SelectItem>
              <SelectItem value="received">Received</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filter chip: Upcoming Deliveries */}
      {upcomingFilter && !statusFilter && (
        <div data-testid="upcoming-filter-chip" className="flex items-center gap-2 bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.3)] rounded-sm px-3 py-2 w-fit">
          <Calendar className="w-4 h-4 gold-text" />
          <span className="text-sm gold-text font-medium">Upcoming Deliveries</span>
          <span className="text-xs text-muted-foreground">— sorted by delivery date</span>
          <button
            onClick={clearUpcomingFilter}
            data-testid="clear-upcoming-filter"
            className="ml-1 text-muted-foreground hover:text-foreground"
            aria-label="Clear filter"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="block md:hidden space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-card rounded-sm animate-pulse" />
          ))
        ) : slips.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
            No slips found
          </div>
        ) : slips.map((slip) => (
          <Card 
            key={slip.id} 
            data-testid={`slip-card-${slip.slip_number}`}
            className="luxury-card rounded-sm cursor-pointer active:scale-[0.99] transition-transform"
            onClick={() => navigate(`/slips/${slip.id}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="font-medium gold-text text-sm">{slip.slip_number}</span>
                <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                  {statusLabels[slip.status]}
                </Badge>
              </div>
              <p className="text-sm text-foreground font-medium">{slip.customer_name}</p>
              <p className="text-xs text-muted-foreground">{slip.customer_phone}</p>
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span>{slip.items?.length || 0} item(s) • {slip.worker_name || "No worker"}</span>
                <span>{slip.delivery_date ? format(subDays(parseISO(slip.delivery_date), 1), "dd MMM") : "-"}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop Table View */}
      <Card className="luxury-card rounded-sm hidden md:block">
        <CardContent className="p-0">
          <Table className="luxury-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Slip No.</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Customer</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Items</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Worker</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Delivery</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={7}><div className="h-8 bg-muted/20 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : slips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No slips found
                  </TableCell>
                </TableRow>
              ) : slips.map((slip) => (
                <TableRow key={slip.id} data-testid={`slip-row-${slip.slip_number}`} className="border-border cursor-pointer" onClick={() => navigate(`/slips/${slip.id}`)}>
                  <TableCell className="font-medium gold-text text-sm">{slip.slip_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-foreground">{slip.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{slip.customer_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{slip.items?.length || 0} item(s)</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{slip.worker_name || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                      {statusLabels[slip.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {slip.delivery_date ? format(subDays(parseISO(slip.delivery_date), 1), "dd MMM yyyy") : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
